import React from "react";
import { Box, Stack, Chip, Collapse, IconButton } from "@mui/material";
import {
	CheckCircleOutlined,
	ExpandMoreOutlined,
	ExpandLessOutlined,
	ErrorOutlineOutlined,
	WarningAmberOutlined,
} from "@mui/icons-material";
import styles from "./index.module.scss";

// A step where the user still has to enter information shows "Missing" (red) instead of
// "In progress" until it is complete.
export const inputStepStatus = (status) => (status === "active" ? "missing" : status);

// status: "pending" | "active" | "missing" | "complete" | "error" | "warning"
const QualifyAccordion = ({
	stepLabel,
	title,
	subTitle,
	status,
	children,
	isOpen,
	onToggle,
	scrollableBody,
	// Called on the user's first key press / mouse press / paste inside the body (not the
	// header) — lets a page tell the user's own edits apart from data loading in.
	onInteract,
}) => {
	const statusConfig = {
		pending: {
			borderClass: styles.borderPending,
			icon: <Box className={styles.stepCirclePending}>{stepLabel}</Box>,
			chip: null,
		},
		active: {
			borderClass: styles.borderActive,
			icon: <Box className={styles.stepCircleActive}>{stepLabel}</Box>,
			chip: <Chip label="In progress" size="small" className={styles.chipActive} />,
		},
		// Required information still missing (Dashboard sections).
		missing: {
			borderClass: styles.borderError,
			icon: <Box className={styles.stepCircleMissing}>{stepLabel}</Box>,
			chip: <Chip label="Missing" size="small" className={styles.chipError} />,
		},
		complete: {
			borderClass: styles.borderComplete,
			icon: <CheckCircleOutlined className={styles.iconComplete} />,
			chip: <Chip label="Complete" size="small" className={styles.chipComplete} />,
		},
		error: {
			borderClass: styles.borderError,
			icon: <ErrorOutlineOutlined className={styles.iconError} />,
			chip: <Chip label="Error" size="small" className={styles.chipError} />,
		},
		warning: {
			borderClass: styles.borderWarning,
			icon: <WarningAmberOutlined className={styles.iconWarning} />,
			chip: <Chip label="Review" size="small" className={styles.chipWarning} />,
		},
	};

	const cfg = statusConfig[status] || statusConfig.pending;

	return (
		<Box className={`${styles.accordionRoot} ${cfg.borderClass}`}>
			<Stack
				direction="row"
				alignItems="center"
				className={styles.header}
				onClick={status !== "pending" ? onToggle : undefined}
				sx={{ cursor: status !== "pending" ? "pointer" : "default" }}
			>
				<Box className={styles.iconWrapper}>{cfg.icon}</Box>
				<Stack direction="column" flex={1} ml={1.5}>
					<span className={styles.titleText}>{title}</span>
					{subTitle && <span className={styles.subTitleText}>{subTitle}</span>}
				</Stack>
				{cfg.chip}
				{status !== "pending" && (
					<IconButton size="small" sx={{ ml: 1 }}>
						{isOpen ? (
							<ExpandLessOutlined fontSize="small" />
						) : (
							<ExpandMoreOutlined fontSize="small" />
						)}
					</IconButton>
				)}
			</Stack>
			<Collapse in={isOpen}>
				<Box
					className={styles.body}
					style={scrollableBody ? { maxHeight: "60vh", overflowY: "auto", overflowX: "hidden" } : undefined}
					onKeyDownCapture={onInteract}
					onMouseDownCapture={onInteract}
					onPasteCapture={onInteract}
				>
					{children}
				</Box>
			</Collapse>
		</Box>
	);
};

export default QualifyAccordion;
